import { useState } from 'react';
import type { SetStateAction } from 'react';
import {
  CARD_TYPE_ADDITIONAL_INFO_TYPES,
  CARD_TYPE_MESSAGE_ACTION_TEXT_MAX_LENGTH,
  CARD_TYPE_URI_MAX_LENGTH,
} from 'constants/cardTypeMessageTemplates';
import { lineStrings } from 'constants/strings';
import { useOrganizationScopeOrNull } from 'components/OrganizationScope/context';
import {
  ECardTypeActionType,
  ECardTypeTemplateId,
  TCardTypeAction,
  TCardTypeAdditionalInfoType,
  TCardTypeCard,
  TCardTypePrice,
  TCardTypeTag,
} from 'types/cardTypeMessage';
import {
  ensureCardActions,
  getCardImages,
  syncCardPrimaryImage,
  uploadCardImage,
} from 'utils/cardTypeMessage';
import { validateCardTypeImageFile } from 'utils/cardTypeMessageImage';
import type { TCardTypeImageValidationError } from 'utils/cardTypeMessageImage';
import {
  cardTypeDefaultAction2,
  cardTypeDefaultPrice2,
} from 'utils/cardTypeMessageDefaults';
import { CardTypeMessageImageUpload } from './CardTypeMessageImageGrid';
import {
  CharCountInput,
  TagColorPicker,
  ToggleFormRow,
} from './CardTypeMessageFormFields';

import type {
  TCardTypeActionFieldErrors,
  TCardTypeCardFieldErrors,
} from './validateCardTypeMessageEditor';

const strings = lineStrings.management.cardTypeMessage;

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-600" role="alert">
      {message}
    </p>
  );
}

type Props = {
  templateId: ECardTypeTemplateId;
  card: TCardTypeCard;
  onChange: (update: SetStateAction<TCardTypeCard>) => void;
  onUploadPendingChange: (pending: boolean) => void;
  onUploadError?: (message: string) => void;
  actionErrors?: Record<number, TCardTypeActionFieldErrors>;
  cardFieldErrors?: TCardTypeCardFieldErrors;
};

function getImageValidationErrorMessage(
  error: TCardTypeImageValidationError,
): string {
  switch (error) {
    case 'invalidType':
      return strings.errors.imageInvalidType;
    case 'tooLarge':
      return strings.errors.imageTooLarge;
    case 'dimensionsTooLarge':
      return strings.errors.imageDimensionsTooLarge;
    case 'decodeFailed':
      return strings.errors.imageDecodeFailed;
  }
}

function ActionFields({
  index,
  action,
  onChange,
  errors,
}: {
  index: number;
  action: TCardTypeAction;
  onChange: (action: TCardTypeAction) => void;
  errors?: TCardTypeActionFieldErrors;
}) {
  const sectionLabel = index === 0 ? strings.action1 : strings.action2;

  return (
    <div className="grid gap-3 rounded-xl border border-border/60 bg-[#F9FAFB] p-4">
      <label className="flex cursor-pointer items-center gap-3 text-sm font-semibold text-ink">
        <input
          type="checkbox"
          checked={action.enabled}
          onChange={(e) => onChange({ ...action, enabled: e.target.checked })}
          className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
        />
        {sectionLabel}
      </label>
      <div className="grid gap-3">
        <CharCountInput
          label=""
          value={action.label}
          maxLength={15}
          countMode="grapheme"
          placeholder={strings.enterActionLabel}
          onChange={(label) => onChange({ ...action, label })}
        />
        <FieldError message={errors?.label} />
        <label className="grid gap-1.5 text-sm font-semibold text-ink">
          {strings.actionType}
          <select
            value={action.actionType}
            onChange={(e) =>
              onChange({
                ...action,
                actionType: e.target.value as TCardTypeAction['actionType'],
              })
            }
            className="rounded-xl border border-border bg-white p-3 text-sm font-normal outline-none focus:border-accent"
          >
            <option value="">{strings.actionTypeSelect}</option>
            <option value={ECardTypeActionType.Text}>
              {strings.actionTypeText}
            </option>
            <option value={ECardTypeActionType.Uri}>
              {strings.actionTypeUri}
            </option>
          </select>
          <FieldError message={errors?.actionType} />
        </label>
        {action.actionType === ECardTypeActionType.Uri ? (
          <div>
            <CharCountInput
              label={strings.actionTypeUri}
              value={action.uri ?? ''}
              maxLength={CARD_TYPE_URI_MAX_LENGTH}
              onChange={(uri) => onChange({ ...action, uri })}
              placeholder={strings.enterUri}
            />
            <FieldError message={errors?.uri} />
          </div>
        ) : null}
        {action.actionType === ECardTypeActionType.Text ? (
          <div>
            <CharCountInput
              label={strings.actionTypeText}
              value={action.text ?? ''}
              maxLength={CARD_TYPE_MESSAGE_ACTION_TEXT_MAX_LENGTH}
              countMode="grapheme"
              multiline
              rows={6}
              onChange={(text) => onChange({ ...action, text })}
              placeholder={strings.enterText}
            />
            <FieldError message={errors?.text} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function TagSection({
  tag,
  onChange,
  error,
}: {
  tag: TCardTypeTag;
  onChange: (tag: TCardTypeTag) => void;
  error?: string;
}) {
  return (
    <ToggleFormRow
      checked={tag.enabled}
      onChange={(enabled) => onChange({ ...tag, enabled })}
      label={strings.tag}
    >
      <div className="grid gap-3">
        <CharCountInput
          label=""
          value={tag.text}
          maxLength={12}
          placeholder={strings.enterTagText}
          onChange={(text) => onChange({ ...tag, text })}
        />
        <FieldError message={error} />
        <TagColorPicker
          value={tag.color}
          onChange={(color) => onChange({ ...tag, color })}
        />
      </div>
    </ToggleFormRow>
  );
}

function PriceFields({
  price,
  onChange,
}: {
  price: TCardTypePrice;
  onChange: (price: TCardTypePrice) => void;
}) {
  return (
    <div className="grid gap-3">
      <CharCountInput
        label={strings.priceLabel}
        value={price.label ?? ''}
        maxLength={15}
        placeholder={strings.enterPriceLabel}
        onChange={(label) => onChange({ ...price, label })}
      />
      <div className="flex flex-wrap items-start gap-2">
        <select
          value={price.currency}
          onChange={(e) => onChange({ ...price, currency: e.target.value })}
          className="w-16 rounded-xl border border-border bg-white p-3 text-sm"
        >
          <option value="¥">¥</option>
          <option value="$">$</option>
        </select>
        <div className="min-w-[140px] flex-1">
          <CharCountInput
            label=""
            value={price.value}
            maxLength={15}
            placeholder="000.00"
            onChange={(value) => onChange({ ...price, value })}
          />
        </div>
      </div>
    </div>
  );
}

export function CardTypeMessageCardForm({
  templateId,
  card,
  onChange,
  onUploadPendingChange,
  onUploadError,
  actionErrors,
  cardFieldErrors,
}: Props) {
  const [uploadingSlot, setUploadingSlot] = useState<'a' | 'b' | 'c' | null>(
    null,
  );
  // 画像の検証（デコード）を待つ間に組織が変わっても、開始時の組織の領域へ上げる
  const organizationId = useOrganizationScopeOrNull()?.organizationId ?? null;

  const supportsMultiImage =
    templateId === ECardTypeTemplateId.Products ||
    templateId === ECardTypeTemplateId.Places;

  const imageCount = (supportsMultiImage ? (card.imageCount ?? 3) : 1) as
    | 1
    | 2
    | 3;
  const images = getCardImages(card);

  const price2 = card.price2 ?? cardTypeDefaultPrice2();

  const uploadSlot = async (slot: 'a' | 'b' | 'c', file: File) => {
    setUploadingSlot(slot);
    onUploadPendingChange(true);
    try {
      const validation = await validateCardTypeImageFile(file);
      if (!validation.ok) {
        onUploadError?.(getImageValidationErrorMessage(validation.error));
        return;
      }

      const url = await uploadCardImage(
        file,
        validation.contentType,
        organizationId,
      );
      onChange((currentCard) => {
        const nextImages = { ...getCardImages(currentCard), [slot]: url };
        return syncCardPrimaryImage({
          ...currentCard,
          images: nextImages,
        });
      });
    } catch {
      onUploadError?.(strings.errors.imageUploadFailed);
    } finally {
      setUploadingSlot(null);
      onUploadPendingChange(false);
    }
  };

  const updateAction = (index: number, action: TCardTypeAction) => {
    const actions = [...ensureCardActions(card).actions];
    actions[index] = action;
    onChange({ ...card, actions });
  };

  const cardWithActions = ensureCardActions(card);
  const primaryAction = cardWithActions.actions[0]!;
  const secondaryAction =
    cardWithActions.actions[1] ?? cardTypeDefaultAction2();

  const imagesSection = (
    <div className="rounded-xl border border-border/60 bg-[#F9FAFB] p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-ink">{strings.images}</p>
        {supportsMultiImage ? (
          <select
            value={imageCount}
            onChange={(e) => {
              const count = Number(e.target.value) as 1 | 2 | 3;
              onChange({ ...card, imageCount: count });
            }}
            className="rounded-xl w-[60px] border border-border bg-white px-3 py-2 text-sm 
            font-normal outline-none focus:border-accent"
          >
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
        ) : null}
      </div>
      <CardTypeMessageImageUpload
        templateId={templateId}
        imageCount={imageCount}
        images={images}
        uploadingSlot={uploadingSlot}
        onUpload={(slot, file) => void uploadSlot(slot, file)}
      />
      <FieldError message={cardFieldErrors?.image} />
    </div>
  );

  return (
    <div className="grid min-w-0 gap-4">
      {templateId === ECardTypeTemplateId.People ? imagesSection : null}

      {templateId !== ECardTypeTemplateId.People ? (
        <TagSection
          tag={card.tag!}
          onChange={(tag) => onChange({ ...card, tag })}
          error={cardFieldErrors?.tag}
        />
      ) : null}

      {templateId !== ECardTypeTemplateId.People ? imagesSection : null}

      {templateId === ECardTypeTemplateId.People ? (
        <div>
          <CharCountInput
            label={strings.name}
            value={card.name ?? ''}
            maxLength={20}
            placeholder={strings.enterName}
            onChange={(name) => onChange({ ...card, name })}
          />
          <FieldError message={cardFieldErrors?.name} />
        </div>
      ) : templateId !== ECardTypeTemplateId.Pictures ? (
        <div>
          <CharCountInput
            label={strings.header}
            value={card.header ?? ''}
            maxLength={20}
            placeholder={strings.enterHeader}
            onChange={(header) => onChange({ ...card, header })}
          />
          <FieldError message={cardFieldErrors?.header} />
        </div>
      ) : null}

      {templateId === ECardTypeTemplateId.People ? (
        <>
          {[0, 1, 2].map((i) => {
            const tag = card.tags![i];
            const tagLabels = [strings.tag1, strings.tag2, strings.tag3];
            return (
              <ToggleFormRow
                key={i}
                checked={tag.enabled}
                onChange={(enabled) => {
                  const tags = [...(card.tags ?? [])] as NonNullable<
                    typeof card.tags
                  >;
                  tags[i] = { ...tag, enabled };
                  onChange({ ...card, tags });
                }}
                label={tagLabels[i]}
              >
                <div className="grid gap-3">
                  <CharCountInput
                    label=""
                    value={tag.text}
                    maxLength={12}
                    placeholder={strings.enterTagText}
                    onChange={(text) => {
                      const tags = [...(card.tags ?? [])] as NonNullable<
                        typeof card.tags
                      >;
                      tags[i] = { ...tag, text };
                      onChange({ ...card, tags });
                    }}
                  />
                  <TagColorPicker
                    value={tag.color}
                    onChange={(color) => {
                      const tags = [...(card.tags ?? [])] as NonNullable<
                        typeof card.tags
                      >;
                      tags[i] = { ...tag, color };
                      onChange({ ...card, tags });
                    }}
                  />
                  <FieldError message={cardFieldErrors?.tags?.[i as 0 | 1 | 2]} />
                </div>
              </ToggleFormRow>
            );
          })}
        </>
      ) : null}

      {templateId === ECardTypeTemplateId.Places ? (
        <ToggleFormRow
          checked={card.address!.enabled}
          onChange={(enabled) =>
            onChange({
              ...card,
              address: { ...card.address!, enabled },
            })
          }
          label={strings.address}
        >
          <div className="grid gap-2">
            <CharCountInput
              label=""
              value={card.address!.text}
              maxLength={60}
              placeholder={strings.enterAddress}
              multiline
              onChange={(text) =>
                onChange({
                  ...card,
                  address: { ...card.address!, text },
                })
              }
            />
            <FieldError message={cardFieldErrors?.address} />
          </div>
        </ToggleFormRow>
      ) : null}

      {templateId === ECardTypeTemplateId.Places ? (
        <ToggleFormRow
          checked={card.additionalInfo!.enabled}
          onChange={(enabled) =>
            onChange({
              ...card,
              additionalInfo: { ...card.additionalInfo!, enabled },
            })
          }
          label={strings.additionalInfo}
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
            <select
              value={card.additionalInfo!.infoType}
              onChange={(e) =>
                onChange({
                  ...card,
                  additionalInfo: {
                    ...card.additionalInfo!,
                    infoType: e.target.value as TCardTypeAdditionalInfoType,
                  },
                })
              }
              className="w-full shrink-0 rounded-xl border border-border bg-white p-3 text-sm outline-none focus:border-accent sm:w-32"
            >
              {CARD_TYPE_ADDITIONAL_INFO_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <div className="min-w-0 flex-1">
              <CharCountInput
                label=""
                value={card.additionalInfo!.text}
                maxLength={30}
                placeholder={strings.enterInfoDetails}
                onChange={(text) =>
                  onChange({
                    ...card,
                    additionalInfo: { ...card.additionalInfo!, text },
                  })
                }
              />
            </div>
          </div>
          <FieldError message={cardFieldErrors?.additionalInfo} />
        </ToggleFormRow>
      ) : null}

      {(templateId === ECardTypeTemplateId.Products ||
        templateId === ECardTypeTemplateId.People) && (
        <ToggleFormRow
          checked={card.description!.enabled}
          onChange={(enabled) =>
            onChange({
              ...card,
              description: { ...card.description!, enabled },
            })
          }
          label={strings.description}
        >
          <CharCountInput
            label=""
            value={card.description!.text}
            maxLength={60}
            placeholder={strings.enterDescription}
            multiline
            onChange={(text) =>
              onChange({
                ...card,
                description: { ...card.description!, text },
              })
            }
          />
          <FieldError message={cardFieldErrors?.description} />
        </ToggleFormRow>
      )}

      {templateId === ECardTypeTemplateId.Products ? (
        <>
          <ToggleFormRow
            checked={card.price!.enabled}
            onChange={(enabled) =>
              onChange({
                ...card,
                price: { ...card.price!, enabled },
              })
            }
            label={strings.price}
          >
            <PriceFields
              price={card.price!}
              onChange={(price) => onChange({ ...card, price })}
            />
            <FieldError message={cardFieldErrors?.price} />
          </ToggleFormRow>
          <ToggleFormRow
            checked={price2.enabled}
            onChange={(enabled) =>
              onChange({
                ...card,
                price2: { ...price2, enabled },
              })
            }
            label={strings.price2}
          >
            <PriceFields
              price={price2}
              onChange={(nextPrice2) =>
                onChange({ ...card, price2: nextPrice2 })
              }
            />
            <FieldError message={cardFieldErrors?.price2} />
          </ToggleFormRow>
        </>
      ) : null}

      <ActionFields
        index={0}
        action={primaryAction}
        onChange={(next) => updateAction(0, next)}
        errors={actionErrors?.[0]}
      />
      {templateId !== ECardTypeTemplateId.Pictures ? (
        <ActionFields
          index={1}
          action={secondaryAction}
          onChange={(next) => updateAction(1, next)}
          errors={actionErrors?.[1]}
        />
      ) : null}
    </div>
  );
}
